/**
 * 装配组件联动 transform 对象解析工具。
 *
 * 文件职责：
 * 1. 收口直接拖拽、能力探测与线性阵列联动对象解析；
 * 2. 保证同一次组件变换只作用在最外层可写根对象，避免父子对象重复叠加 transform；
 * 3. 不持有 Viewer 或 Object3D 生命周期，只消费调用方传入的当前场景对象。
 */

import * as THREE from 'three';
import { createComponentInstanceSnapshotFromObject } from '../component-instance';

/**
 * 装配组件联动解析宿主。
 */
export interface AssemblyComponentTransformLinkResolverHost {
    /**
     * 线性阵列管理器。
     *
     * 说明：线性阵列实例需要和源实例一同变换；缺失时按普通单组件处理。
     */
    readonly linearComponentPatternManager?: {
        /**
         * 根据当前 transform 根解析需要联动的组件对象。
         *
         * @param targetObject 当前被拖拽或探测的组件根。
         * @returns 需要一同写入 transform 的对象集合。
         */
        resolveTransformLinkedObjects?: (targetObject: THREE.Object3D) => readonly THREE.Object3D[];
        //------ 线性阵列同源旋转锚点 开始------
        /**
         * 根据当前 transform 根解析联动上下文。
         *
         * @param targetObject 当前被拖拽或探测的组件根。
         * @returns 联动对象与旋转锚点。
         */
        resolveTransformLinkContext?: (targetObject: THREE.Object3D) => AssemblyComponentTransformLinkContext;
        //------ 线性阵列同源旋转锚点 结束------
    } | null;
    /**
     * 装配 Mate 控制器。
     *
     * 说明：多重 Mate 把若干浮动组件约束成刚性组合时，Move/Rotate 入口需要一次性写入整组 transform。
     */
    readonly assemblyMateController?: {
        /**
         * 解析当前组件所在的全浮动刚性 Mate 组合。
         *
         * @param input 组件实例 ID 输入。
         * @returns 需要共同写入 transform 的对象集合。
         */
        resolveFloatingRigidLinkedTransformObjectsForTransform?: (input: {
            readonly componentInstanceId: string;
        }) => readonly THREE.Object3D[];
    } | null;
}

//------ 线性阵列同源旋转锚点 开始------
/**
 * 装配组件 transform 联动上下文。
 *
 * 设计说明：
 * 1. `linkedObjects` 表示同一次移动或旋转需要写入 transform 的组件根；
 * 2. `rotationAnchorObject` 表示非约束自由旋转时使用的共同旋转中心来源对象；
 * 3. 普通零部件没有阵列语义时，旋转锚点为空，保持原本“绕自身中心”的行为。
 */
export interface AssemblyComponentTransformLinkContext {
    /** 同一次 transform 应一起写入的对象集合。 */
    readonly linkedObjects: readonly THREE.Object3D[];
    /** 自由旋转时用于解析共同旋转中心的对象；无阵列联动时为空。 */
    readonly rotationAnchorObject?: THREE.Object3D | null;
    /**
     * 自由旋转时用于计算共同包围盒中心的对象集合。
     *
     * 说明：线性阵列仍优先使用 `rotationAnchorObject`；Mate 刚性组合使用整组 bbox 中心。
     */
    readonly rotationPivotObjects?: readonly THREE.Object3D[] | null;
}

/**
 * 已归一化的装配组件 transform 联动上下文。
 */
export interface ResolvedAssemblyComponentTransformLinkContext {
    /** 去重且剔除嵌套子节点后的联动对象集合。 */
    readonly linkedObjects: THREE.Object3D[];
    /** 自由旋转时用于解析共同旋转中心的对象；无阵列联动时为空。 */
    readonly rotationAnchorObject: THREE.Object3D | null;
    /** 自由旋转时用于计算共同 bbox 中心的对象集合；无 Mate 刚性组合时为空。 */
    readonly rotationPivotObjects: THREE.Object3D[];
}
//------ 线性阵列同源旋转锚点 结束------

/**
 * 判断对象是否位于指定根对象的子树中。
 *
 * @param object 待判断对象。
 * @param root 候选根对象。
 * @returns 对象位于根对象子树内时返回 `true`。
 */
function isDescendantOfTarget(object: THREE.Object3D, root: THREE.Object3D): boolean {
    let current: THREE.Object3D | null = object.parent;
    while (current) {
        if (current === root) {
            return true;
        }
        current = current.parent;
    }
    return false;
}

//------ 线性阵列同源旋转锚点 开始------
/**
 * 归一化联动对象集合。
 *
 * @param targetObject 当前组件 transform 根。
 * @param linkedObjects 联动解析器返回的对象集合。
 * @returns 去重且剔除嵌套子节点后的联动对象集合。
 */
function normalizeLinkedTransformObjects(
    targetObject: THREE.Object3D,
    linkedObjects: readonly THREE.Object3D[],
): THREE.Object3D[] {
    const dedupedObjects = new Map<string, THREE.Object3D>();
    linkedObjects.forEach((object) => {
        dedupedObjects.set(object.uuid, object);
    });
    if (!dedupedObjects.has(targetObject.uuid)) {
        dedupedObjects.set(targetObject.uuid, targetObject);
    }

    const normalizedLinkedObjects = [...dedupedObjects.values()];
    return normalizedLinkedObjects.filter((object) => {
        return !normalizedLinkedObjects.some((candidateRoot) => {
            if (candidateRoot === object) {
                return false;
            }

            return isDescendantOfTarget(object, candidateRoot);
        });
    });
}

/**
 * 解析 Mate 刚性组合需要联动写入的对象集合。
 *
 * @param host 装配联动能力宿主。
 * @param targetObject 当前组件 transform 根。
 * @returns Mate 刚性组合对象集合；不存在时返回空数组。
 */
function resolveMateRigidLinkedTransformObjects(
    host: AssemblyComponentTransformLinkResolverHost,
    targetObject: THREE.Object3D,
): readonly THREE.Object3D[] {
    const controller = host.assemblyMateController;
    if (!controller?.resolveFloatingRigidLinkedTransformObjectsForTransform) {
        return [];
    }

    const snapshot = createComponentInstanceSnapshotFromObject({
        object: targetObject as never,
    });
    return controller.resolveFloatingRigidLinkedTransformObjectsForTransform({
        componentInstanceId: snapshot.componentInstanceId,
    });
}

/**
 * 解析当前组件变换需要联动写入的上下文。
 *
 * @param host 线性阵列等联动能力宿主。
 * @param targetObject 当前组件 transform 根。
 * @returns 已归一化的联动上下文。
 */
export function resolveAssemblyComponentTransformLinkContext(
    host: AssemblyComponentTransformLinkResolverHost,
    targetObject: THREE.Object3D,
): ResolvedAssemblyComponentTransformLinkContext {
    const patternContext = host.linearComponentPatternManager?.resolveTransformLinkContext?.(targetObject) ?? null;
    const patternLinkedObjects = patternContext?.linkedObjects
        ?? host.linearComponentPatternManager?.resolveTransformLinkedObjects?.(targetObject)
        ?? [targetObject];
    const mateRigidLinkedObjects = resolveMateRigidLinkedTransformObjects(host, targetObject);
    const linkedObjects = normalizeLinkedTransformObjects(
        targetObject,
        [...patternLinkedObjects, ...mateRigidLinkedObjects],
    );
    const rotationPivotObjects = normalizeLinkedTransformObjects(
        targetObject,
        patternContext?.rotationPivotObjects ?? mateRigidLinkedObjects,
    );

    return {
        linkedObjects,
        rotationAnchorObject: patternContext?.rotationAnchorObject ?? null,
        rotationPivotObjects: rotationPivotObjects.length > 1 ? rotationPivotObjects : [],
    };
}
//------ 线性阵列同源旋转锚点 结束------

/**
 * 解析当前组件变换需要联动写入的对象集合。
 *
 * @param host 线性阵列等联动能力宿主。
 * @param targetObject 当前组件 transform 根。
 * @returns 去重且剔除嵌套子节点后的联动对象集合。
 */
export function resolveAssemblyComponentLinkedTransformObjects(
    host: AssemblyComponentTransformLinkResolverHost,
    targetObject: THREE.Object3D,
): THREE.Object3D[] {
    return resolveAssemblyComponentTransformLinkContext(host, targetObject).linkedObjects;
}
